import React from 'react'
import { create } from 'jss'
import rtl from 'jss-rtl'
import { BrowserRouter as Router } from 'react-router-dom'
import { createGenerateClassName, createMuiTheme, fade } from '@material-ui/core/styles'
import { StylesProvider, ThemeProvider as MuiThemeProvider, jssPreset } from '@material-ui/styles'
import { LocalStorageService } from '../Services/LocalStorageService'
import Configuration from '../Services/Api/Configuration'

// Configure JSS
const jss = create({ plugins: [...jssPreset().plugins, rtl()] })
const generateClassName = createGenerateClassName()
let { DefaultFont, DefaultDirection } = Configuration

const ThemeProvider = ({ children }) => {
  const [font, setFont] = React.useState(DefaultFont)
  const [storageDirection, setStorageDirection] = React.useState(DefaultDirection)

  let isSubscribed = false
  React.useEffect(() => {
    // eslint-disable-next-line
    isSubscribed = true
    LocalStorageService.get('EInvoicingFontName').then((storageFont) => {
      if (isSubscribed) {
        setFont(storageFont)
      }
    })
    LocalStorageService.get('EInvoicingDirection').then((storageDirection) => {
      if (isSubscribed) {
        setStorageDirection(storageDirection)
      }
    })
    return () => isSubscribed = false
  }, [])

  let defaultTheme = createMuiTheme()
  let theme = createMuiTheme({
    direction: storageDirection,
    palette: {
      primary: {
        border: '#a1d4ff',
        lighter: '#96bedf',
        light: '#75abde',
        main: '#427cac',
        dark: '#00507d',
        contrastText: '#fff'
      },
      // primary: {
      //   light: '#41569c',
      //   main: '#002e6d',
      //   dark: '#000441',
      //   contrastText: '#fff'
      // },
      secondary: {
        light: '#ff6146',
        main: '#e1251b',
        dark: '#a70000',
        contrastText: '#fff'
      },
      // secondary: {
      //   light: '#ffca62',
      //   main: '#e39932',
      //   dark: '#ac6b00',
      //   contrastText: '#fff'
      // },
      error: {
        light: '#e57373',
        main: '#f44336',
        dark: '#d32f2f',
        contrastText: '#fff'
      },
      success: {
        light: '#81c784',
        main: '#4caf50',
        dark: '#388e3c',
        contrastText: fade('#000', 0.87)
      },
      info: {
        light: '#64b5f6',
        main: '#2196f3',
        dark: '#1976d2',
        contrastText: '#fff'
      },
      warning: {
        light: '#ffb74d',
        main: '#ff9800',
        dark: '#f57c00',
        contrastText: fade('#000', 0.87)
      },
      text: {
        primary: '#5E5E5E',
        secondary: '#6A6A6A',
        light: '#989898',
        hint: '#98a9bc'
      },
      colors: {
        logoColor: '#D0A15E',
        offWhite: '#F1F1F1',
        body: '#F5F5F5',
        table: '#e0e0e0',
        footer: '#6d6d6d',
        border: '#EEEEEE',
        border2: '#74727f'
      }
    },
    typography: {
      'fontFamily': font,
      'htmlFontSize': 16,
      'fontSize': 14,
      'fontWeightLight': 300,
      'fontWeightRegular': 400,
      'fontWeightSemiBold': 600,
      'fontWeightBold': 700
    },
    customShadows: {
      widget:
        '0px 3px 11px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A',
      widgetDark:
        '0px 3px 18px 0px #4558A3B3, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A',
      widgetWide:
        '0px 12px 33px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A'
    },
    customBreakpoints: {
      mdl: 1200
    },
    overrides: {
      MUIDataTableBodyRow: {
        root: {
          [defaultTheme.breakpoints.down('xs')]: {
            display: 'block'
          }
        },
        responsiveSimple: {
          [defaultTheme.breakpoints.down('xs')]: {
            borderTopWidth: 1,
            borderBottom: 0,
            '&:nth-child(odd)':{
              backgroundColor: '#F1F1F1',
            }
          }
        }
      },
      MuiTableCell: {
        root: {
          padding: defaultTheme.spacing(1.5),
          [defaultTheme.breakpoints.down('xs')]: {
            display: 'flex !important',
            alignItems: 'center',
            padding: defaultTheme.spacing(1, 1.5)
          }
        }
      },
      MUIDataTableBodyCell: {
        simpleHeader: {
          [defaultTheme.breakpoints.down('xs')]: {
            // width: 90,
            flex: '0 0 110px',
            '& + div': {
              width: 'calc( 100% - 110px)'
            }
          }
        }
        // simpleCell: {
        //   [defaultTheme.breakpoints.down('sm')]: {
        //
        //   }
        // }
      },
      MUIDataTableHeadCell: {
        root: {
          fontWeight: defaultTheme.typography.fontWeightBold,
          whiteSpace: 'pre',
          lineHeight: 1.5
        }
      },
      MUIDataTable: {
        paper: {
          width: '100%',
          paddingBottom: defaultTheme.spacing(1.5),
          [defaultTheme.breakpoints.up('sm')]: {
            paddingLeft: defaultTheme.spacing(1.5),
            paddingRight: defaultTheme.spacing(1.5)
          }
        },
        tableRoot: {
          [defaultTheme.breakpoints.down('xs')]: {
            '&, & tbody': {
              display: 'block'
            }
          }
        }
      },
      MUIDataTableFilter: {
        gridListTile: {
          marginTop: 0
        }
      },
      MUIDataTableToolbar: {
        root: {
          [defaultTheme.breakpoints.down('sm')]: {
            display: 'block'
          },
          [defaultTheme.breakpoints.up('sm')]: {
            paddingTop: defaultTheme.spacing(1),
            paddingBottom: defaultTheme.spacing(1),
            marginLeft: defaultTheme.spacing(-1.5),
            marginRight: defaultTheme.spacing(-1.5),
            borderBottom: '1px solid #e0e0e0'
          }
        },
        filterPaper: {
          '&> button': {
            left: 'auto !important',
            right: '4px !important'
          }
        },
        filterCloseIcon: {
          left: 'auto !important',
          right: '4px !important'
        }
      }
    }
  })

  return (
    <StylesProvider jss={jss} generateClassName={generateClassName}>
      <MuiThemeProvider theme={theme}>
        <Router>
          {children}
        </Router>
      </MuiThemeProvider>
    </StylesProvider>
  )
}

export { ThemeProvider }
